'use client';

import Icon from '@ant-design/icons';
import { Popover } from 'antd';
import { useMemo, useRef, useState } from 'react';

import ImageSvg from '@/components/Icon/svgs/image_fill.svg';
import MicroSvg from '@/components/Icon/svgs/microphoneStageFill.svg';
import PersonFillSvg from '@/components/Icon/svgs/person-fill.svg';
import TopicSvg from '@/components/Icon/svgs/topic-filled.svg';
import { useTranslation } from '@/i18n/client';
import type { PlayModalType } from '@/ui/HomePage/HomepageContext';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';

import styles from './index.module.less';

interface AddPopoverProps {
  children: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  onUploadClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onUploadChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showPlayOptions?: boolean;
  disabled?: boolean;
}

const AddPopover = ({
  children,
  onUploadClick,
  onUploadChange,
  showPlayOptions = false,
  disabled = false
}: AddPopoverProps): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { homepageData, setModalConfig, setPlayModalOpen } = useHomepageContext();

  const originChoose = useMemo(() => {
    return homepageData
      ? {
          ...homepageData,
          name: homepageData.username || '',
          available: true
        }
      : null;
  }, [homepageData]);

  const handlePlayClick = (type: PlayModalType): void => {
    setModalConfig({
      type,
      originChoose
    });
    setPlayModalOpen(true);
    setOpen(false);
  };

  const handleUploadItemClick = () => {
    inputRef.current?.click();

    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onUploadChange) {
      onUploadChange(e);
    }
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (onUploadClick) {
      onUploadClick(e);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const content = (
    <div className={styles.popoverContent}>
      {/* 添加图片或文件 */}
      <div className={styles.menuItem} onClick={handleUploadItemClick}>
        <Icon className={styles.menuIcon} component={ImageSvg} />
        <span className={styles.menuText}>{t('chatApp.input.addImageOrFile')}</span>
      </div>

      {/* AI 玩法选项 - 仅在 showPlayOptions 为 true 时显示 */}
      {showPlayOptions && (
        <>
          <div className={styles.divider} />

          {/* AI脑洞 */}
          <div className={styles.menuItem} onClick={() => handlePlayClick('topic')}>
            <div className={styles.playIconWrapper}>
              <Icon className={styles.playIcon} component={TopicSvg} />
            </div>
            <span className={styles.menuText}>{t('homepage.aiTopic')}</span>
          </div>

          {/* AI合拍 */}
          <div className={styles.menuItem} onClick={() => handlePlayClick('snap')}>
            <div className={styles.playIconWrapper}>
              <Icon className={styles.playIcon} component={PersonFillSvg} />
            </div>
            <span className={styles.menuText}>{t('snap.create.aiBooth')}</span>
          </div>

          {/* AI合唱 */}
          <div className={styles.menuItem} onClick={() => handlePlayClick('chorus')}>
            <div className={styles.playIconWrapper}>
              <Icon className={styles.playIcon} component={MicroSvg} />
            </div>
            <span className={styles.menuText}>{t('homepage.aiChorus')}</span>
          </div>
        </>
      )}

      {/* 隐藏的文件上传 input */}
      <input
        ref={inputRef}
        accept=".doc,.docx,.pdf,.txt,.md,image/png,image/jpeg"
        className={styles.hiddenInput}
        multiple
        onChange={handleInputChange}
        onClick={handleInputClick}
        type="file"
      />
    </div>
  );

  if (disabled) {
    return <>{typeof children === 'function' ? children(false) : children}</>;
  }

  const renderedChildren = typeof children === 'function' ? children(open) : children;

  return (
    <Popover
      arrow={false}
      content={content}
      onOpenChange={handleOpenChange}
      open={open}
      overlayClassName={styles.popoverOverlay}
      placement="topLeft"
      trigger="click"
    >
      {renderedChildren}
    </Popover>
  );
};

export default AddPopover;
